/// <reference types="@types/google.maps" />
import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface AddressComponents {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (components: AddressComponents) => void;
  placeholder?: string;
  className?: string;
  onBlur?: () => void;
}

declare global {
  interface Window {
    google: typeof google;
    initGoogleMaps?: () => void;
  }
}

let googleMapsLoaded = false;
let googleMapsLoading = false;
let cachedApiKey: string | null = null;
const loadCallbacks: (() => void)[] = [];

async function fetchApiKey(): Promise<string> {
  if (cachedApiKey) return cachedApiKey;

  const response = await fetch("/api/config/maps", { credentials: "include" });
  if (!response.ok) {
    throw new Error("Failed to fetch Maps API key");
  }
  const data = await response.json();
  cachedApiKey = data.apiKey;
  return data.apiKey;
}

async function loadGoogleMapsScript(): Promise<void> {
  if (googleMapsLoaded) {
    return;
  }

  return new Promise(async (resolve, reject) => {
    loadCallbacks.push(resolve);

    if (googleMapsLoading) {
      return;
    }

    googleMapsLoading = true;

    try {
      const apiKey = await fetchApiKey();

      window.initGoogleMaps = () => {
        googleMapsLoaded = true;
        loadCallbacks.forEach((cb) => cb());
        loadCallbacks.length = 0;
      };

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        googleMapsLoading = false;
        reject(new Error("Failed to load Google Maps script"));
      };
      document.head.appendChild(script);
    } catch (error) {
      googleMapsLoading = false;
      reject(error);
    }
  });
}

const STREET_NUMBER_PREFIX_RE = /^\d+\s+\S/;
const STREET_SUFFIX_RE = /\b(st|street|ave|avenue|rd|road|dr|drive|ln|lane|blvd|boulevard|ct|court|way|pl|place|cir|circle|pkwy|parkway|hwy|highway|ter|terrace|trl|trail|sq|square|loop|aly|alley|row|run)\b\.?/i;

function looksLikeStreet(part: string): boolean {
  if (!part) return false;
  if (STREET_NUMBER_PREFIX_RE.test(part)) return true;
  return STREET_SUFFIX_RE.test(part);
}

function parseAddressComponents(
  place: google.maps.places.PlaceResult,
  suggestionDescription?: string,
): AddressComponents {
  const components: AddressComponents = {
    address: "",
    city: "",
    state: "",
    zipCode: "",
  };

  let streetNumber = "";
  let route = "";

  // Candidate city names keyed by their Google component type. Resolved in
  // priority order after the loop so that `locality` always wins when present,
  // regardless of the order components are returned in.
  const cityCandidates: Record<string, string> = {};

  if (place.address_components) {
    for (const component of place.address_components) {
      const types = component.types;

      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      } else if (types.includes("route")) {
        route = component.long_name;
      } else if (types.includes("locality")) {
        cityCandidates.locality = component.long_name;
      } else if (types.includes("sublocality_level_1")) {
        cityCandidates.sublocality_level_1 = component.long_name;
      } else if (types.includes("postal_town")) {
        cityCandidates.postal_town = component.long_name;
      } else if (types.includes("administrative_area_level_3")) {
        cityCandidates.administrative_area_level_3 = component.long_name;
      } else if (types.includes("neighborhood")) {
        cityCandidates.neighborhood = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        components.state = component.short_name;
      } else if (types.includes("postal_code")) {
        components.zipCode = component.long_name;
      }
    }
  }

  // Resolve the city from candidates in priority order. `locality` and
  // `sublocality_level_1` are the canonical sources; the remaining types are
  // fallbacks for suburban, unincorporated, or rural addresses where Google
  // reports the city under a non-`locality` component.
  const cityPriority = [
    "locality",
    "sublocality_level_1",
    "postal_town",
    "administrative_area_level_3",
    "neighborhood",
  ];
  for (const type of cityPriority) {
    if (cityCandidates[type]) {
      components.city = cityCandidates[type];
      break;
    }
  }

  // Prefer street_number + route from address_components.
  if (streetNumber && route) {
    components.address = `${streetNumber} ${route}`;
  } else if (route) {
    components.address = route;
  } else {
    // Derive a street from formatted_address (or suggestion description) by
    // stripping a leading place.name segment and picking the first remaining
    // segment that looks like a street. Never fall back to the business name
    // or the city — leave empty if nothing street-like is found.
    const source = place.formatted_address || suggestionDescription || "";
    if (source) {
      const parts = source.split(",").map((p) => p.trim()).filter(Boolean);
      const businessName = (place.name || "").trim().toLowerCase();
      const cityLower = components.city.trim().toLowerCase();

      // Strip leading business-name segment if present.
      let candidates = parts;
      if (businessName && candidates[0]?.toLowerCase() === businessName) {
        candidates = candidates.slice(1);
      }

      const isCity = (s: string) => cityLower && s.trim().toLowerCase() === cityLower;
      const isBusiness = (s: string) =>
        businessName && s.trim().toLowerCase() === businessName;

      const street = candidates.find(
        (p) => !isCity(p) && !isBusiness(p) && looksLikeStreet(p),
      );

      if (street) {
        components.address = street;
      }
    }
  }

  return components;
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing address...",
  className,
  onBlur,
}: AddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Set when a suggestion was just selected so handleBlur doesn't overwrite
  // the freshly-set street with the stale typed-in text.
  const justSelectedRef = useRef(false);
  // Track the most recently selected street so the value-sync effect doesn't
  // clobber it back to the user-typed text between renders.
  const lastSelectedAddressRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip syncing from parent if we just wrote a different value via a
    // suggestion selection — the parent's update will arrive next render and
    // re-syncing the previous (typed) value here would flicker the field.
    if (
      lastSelectedAddressRef.current !== null &&
      value !== lastSelectedAddressRef.current
    ) {
      return;
    }
    lastSelectedAddressRef.current = null;
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    loadGoogleMapsScript()
      .then(() => {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        const dummyDiv = document.createElement("div");
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        setIsLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load Google Maps:", err);
        setLoadError("Address lookup unavailable");
      });
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    (input: string) => {
      if (!autocompleteService.current || !input.trim()) {
        setSuggestions([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        {
          input,
          types: ["establishment", "geocode"],
          componentRestrictions: { country: "us" },
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    },
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    if (isLoaded) {
      fetchSuggestions(newValue);
    }
  };

  const commitSelection = (components: AddressComponents) => {
    justSelectedRef.current = true;
    lastSelectedAddressRef.current = components.address;
    setInputValue(components.address);
    if (onAddressSelect) {
      // Single, atomic update from the parent — parent writes street + city +
      // state + zip together via its functional setFormData call.
      onAddressSelect(components);
    } else {
      // Fallback when no onAddressSelect is provided.
      onChange(components.address);
    }
    setSuggestions([]);
    setShowSuggestions(false);
    // Clear the just-selected flag after the blur timeout would have fired.
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 350);
  };

  const commitDegradedSelection = (suggestion: google.maps.places.AutocompletePrediction) => {
    // Degraded path: PlacesService unavailable or getDetails failed. We can't
    // resolve city/state/zip, so don't call onAddressSelect (which would clear
    // any values the user already entered). Just write the street via
    // onChange — preferring a street-like segment from the description.
    const parts = suggestion.description
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    const street = parts.find((p) => looksLikeStreet(p)) || parts[0] || suggestion.description;
    justSelectedRef.current = true;
    lastSelectedAddressRef.current = street;
    setInputValue(street);
    onChange(street);
    setSuggestions([]);
    setShowSuggestions(false);
    setTimeout(() => {
      justSelectedRef.current = false;
    }, 350);
  };

  const handleSelectSuggestion = (suggestion: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) {
      commitDegradedSelection(suggestion);
      return;
    }

    placesService.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ["address_components", "formatted_address", "name"],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          commitSelection(parseAddressComponents(place, suggestion.description));
        } else {
          commitDegradedSelection(suggestion);
        }
      }
    );
  };

  const handleBlur = () => {
    setTimeout(() => {
      // If a suggestion was just clicked, skip the reconciliation — the
      // suggestion's onChange/onAddressSelect has already written the right
      // value and our inputValue closure here is the stale typed text.
      if (justSelectedRef.current) {
        onBlur?.();
        return;
      }
      if (inputValue !== value) {
        onChange(inputValue);
      }
      onBlur?.();
    }, 200);
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          setShowSuggestions(true);
          if (isLoaded && inputValue) {
            fetchSuggestions(inputValue);
          }
        }}
        onBlur={handleBlur}
        placeholder={loadError || placeholder}
        className={cn("h-7 border-0 rounded-none text-sm focus-visible:ring-0", className)}
        data-testid="input-address-autocomplete"
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-80 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="px-3 py-2 text-sm cursor-pointer hover-elevate"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectSuggestion(suggestion)}
              data-testid={`suggestion-${suggestion.place_id}`}
            >
              {suggestion.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
